import * as React from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack, Typography } from '@mui/material';
import StatusBox from './StatusBox';
import Tooltip from '../../common/Tooltip';
import DistributionsHeadCells from './DistributionsHeadCells';
import MobileTableRow from '../../common/MobileTableRow';

const unPaidDistributionsStyle = {
  border: 'none',
  '&:before': {
    display: 'none',
  },
  '&:first-of-type': {
    border: 'none',
  },
  borderRadius: 0,
  m: 0,
  mb: '2px',
  backgroundColor: 'mobileTable.dark',
};

export default function MobileAccordion({
  panelName,
  row,
  handleChange,
  expanded,
  unPaidDistributionsOnly,
}) {
  const conditionalStyles = unPaidDistributionsOnly ? unPaidDistributionsStyle : {};
  const distributionsHeadCells = DistributionsHeadCells(unPaidDistributionsOnly)
  return (
    <Accordion
      sx={{
        m: 0,
        mb: '8px',
        borderRadius: '8px',
        backgroundColor: 'mobileTable.light',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'border.dark',
        overflow: 'hidden',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        '&:first-of-type': {
          borderRadius: '8px',
        },
        ...conditionalStyles,
      }}
      expanded={expanded === panelName}
      onChange={() => {
        handleChange(panelName, expanded);
      }}
    >
      <AccordionSummary
        sx={{
          '& > div': {
            fontFamily: 'Inter',
            fontWeight: '700',
            boxShadow: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
            padding: 0,
            margin: 0,
            gap: '3px',
          },
        }}
        expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
      >
        <Stack direction="column">
          <span>{unPaidDistributionsOnly ? row.companyName : row.payee}</span>
          {!unPaidDistributionsOnly && (
            <Typography sx={{ fontSize: '12px', fontWeight: '400' }}>
              Date sent: {row.datePaid}
            </Typography>
          )}
        </Stack>
        <span>{unPaidDistributionsOnly ? row.amount : row.totalSubdistributions}</span>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {distributionsHeadCells.filter((headCell) => headCell.id !== 'fundName').map((headCell) => {
          // Determine excluded IDs based on unPaidDistributionsOnly flag
          const excludedIds = unPaidDistributionsOnly
            ? ['dateSent', 'issueDate', 'amount']
            : ['companyName', 'payee', 'issueDate', 'fundName', 'amount', 'datePaid'];

          // Skip excluded IDs
          if (excludedIds.includes(headCell.id)) {
            return null;
          }

          return (
            <MobileTableRow
              key={headCell.id}
              title={
                <>
                  {headCell.tooltip && <Tooltip title={headCell.tooltip} />}
                  {headCell.label}
                </>
              }
              contents={
                <>
                  {headCell.id === 'status' ? <StatusBox status={row.status} /> : row[headCell.id]}
                </>
              }
            />
          );
        })}
        {'fundName' in row && !unPaidDistributionsOnly && (
          <>
            <MobileTableRow
              sx={{ justifyContent: 'center' }}
              key="fundName"
              unPaidDistributionsOnly={unPaidDistributionsOnly}
              title={row.fundName}
            />
            {row.subDistributions?.map((subRow, index) => (
              <MobileTableRow
                sx={{
                  backgroundColor: index % 2 === 0 ? '#F5F1F9 !important' : 'white !important',
                }}
                contentsSx={{ fontWeight: '700' }}
                key={`${subRow.companyName} - ${subRow.amount}`}
                title={subRow.companyName}
                contents={subRow.amount}
              />
            ))}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

MobileAccordion.propTypes = {
  panelName: PropTypes.string.isRequired,
  row: PropTypes.shape({
    status: PropTypes.string.isRequired,
    payee: PropTypes.string,
    paymentMethod: PropTypes.string,
    datePaid: PropTypes.string,
    subRow: PropTypes.arrayOf(
      PropTypes.shape({
        companyName: PropTypes.string.isRequired,
        amount: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  unPaidDistributionsOnly: PropTypes.bool,
};

MobileAccordion.defaultProps = {
  unPaidDistributionsOnly: false,
};
